import { MortgageFormData, MortgageResult } from '../types/mortgage';

export const calculateMortgageAffordability = (formData: MortgageFormData): MortgageResult => {
  const {
    propertyRegion,
    mortgageTermYears,
    mortgageTermMonths,
    interestRate,
    currentAge,
    retirementAge,
    employedIncome,
    selfEmployedIncome,
    pensionIncome,
    leisureExpenses,
    loanRepayments,
    householdExpenses,
    dependentsCosts
  } = formData;

  // Calculate total annual income
  const totalAnnualIncome = employedIncome + selfEmployedIncome + pensionIncome;
  
  // Calculate total monthly outgoings
  const totalMonthlyOutgoings = leisureExpenses + loanRepayments + householdExpenses + dependentsCosts;
  
  // Calculate total monthly income
  const totalMonthlyIncome = totalAnnualIncome / 12;
  
  // Calculate disposable income
  const disposableIncome = totalMonthlyIncome - totalMonthlyOutgoings;
  
  // Basic eligibility checks
  if (totalAnnualIncome < 15000) {
    return {
      approved: false,
      rejectionReason: "Minimum income requirement not met (£15,000 per year)"
    };
  }
  
  if (currentAge >= retirementAge) {
    return {
      approved: false,
      rejectionReason: "Current age must be less than retirement age"
    };
  }
  
  if (disposableIncome < 500) {
    return {
      approved: false,
      rejectionReason: "Insufficient disposable income after expenses"
    };
  }
  
  // Calculate maximum loan amount using income multiple (typically 4.5x for most lenders)
  const incomeMultiple = totalAnnualIncome <= 30000 ? 4.0 : 
                        totalAnnualIncome <= 50000 ? 4.5 : 
                        totalAnnualIncome <= 75000 ? 5.0 : 5.5;
  
  const maxLoanByIncome = totalAnnualIncome * incomeMultiple;
  
  // Calculate maximum loan based on affordability (35% of gross monthly income)
  const maxAffordablePayment = totalMonthlyIncome * 0.35;
  
  // Calculate total mortgage term in months
  const totalTermMonths = (mortgageTermYears * 12) + mortgageTermMonths;
  
  // Calculate maximum loan based on monthly payment affordability
  const monthlyRate = interestRate / 100 / 12;
  const maxLoanByAffordability = maxAffordablePayment * 
    ((1 - Math.pow(1 + monthlyRate, -totalTermMonths)) / monthlyRate);
  
  // Take the lower of the two maximums
  const maxLoanAmount = Math.min(maxLoanByIncome, maxLoanByAffordability);
  
  // Calculate the actual monthly payment
  const monthlyPayment = maxLoanAmount * 
    (monthlyRate * Math.pow(1 + monthlyRate, totalTermMonths)) / 
    (Math.pow(1 + monthlyRate, totalTermMonths) - 1);
  
  // Additional checks
  if (maxLoanAmount < 50000) {
    return {
      approved: false,
      rejectionReason: "Loan amount too low (minimum £50,000)"
    };
  }
  
  // Check if mortgage will extend beyond retirement age
  const mortgageEndAge = currentAge + (totalTermMonths / 12);
  if (mortgageEndAge > retirementAge) {
    return {
      approved: false,
      rejectionReason: "Mortgage term extends beyond retirement age"
    };
  }
  
  // Additional regional considerations
  let regionalMultiplier = 1.0;
  if (propertyRegion === 'scotland') {
    regionalMultiplier = 1.05; // Slightly higher lending in Scotland
  }
  
  const finalMaxLoan = Math.floor(maxLoanAmount * regionalMultiplier);
  
  return {
    approved: true,
    maxLoanAmount: finalMaxLoan,
    monthlyPayment: Math.round(monthlyPayment),
    contactDetails: {
      phone: "0800 123 4567",
      email: "mortgages@example.com",
      advisorName: "Sarah Johnson"
    }
  };
}; 